/**
 * SoilSessionDetailPage — the soil session detail screen (U3 Task 4).
 *
 * Replaces a screen that rendered a raw pat-card per group with raw
 * <label><input> capture panels. Here: PageHeader + a dense DataTable of the
 * session's groups, a totals row, and a disclosure naming orchards that are
 * in no group (they are silently excluded from the instruction otherwise).
 *
 * Task 5 extends this SAME file with expand-to-capture, per-group sign-off,
 * and cancel-session. None of that is built here — no Cancel session button,
 * no capture UI, no sign-off modal.
 *
 * All row-level decisions (action eligibility, queued/late-signoff row
 * modifiers, totals, ungrouped orchards) live in ./soilSessionDetail — this
 * file is presentation only. See that module's header comment for why there
 * is deliberately no actual-kg column and no variance.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import {
  DataTable, Notice, StatusPill, Field, FormSection, Modal,
} from '../patterns';
import { useMenu } from '../../auth/useMenu';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { soilSessionService, SoilSession } from '../../services/soilSessionService';
import { soilGroupService, SoilGroupCompletePayload } from '../../services/soilGroupService';
import { soilGroupSetService } from '../../services/soilGroupSetService';
import { readPendingSoilGroupIds } from '../../services/soilGroupSyncService';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDateTime } from '../../dateFormat';
import { generateUUID } from '../../utils';
import {
  DetailRow, buildDetailRows, detailTotals, ungroupedOrchards, rowModifierClass,
} from './soilSessionDetail';
import NutritionBackCapturePill from './NutritionBackCapturePill';
import { getTenantInfo } from '../../services/tenantService';
import { isBackCaptureBlocked } from '../../utils/backCaptureWindow';
import './nutrition.css';

// `datetime-local` values are LOCAL wall-clock with no zone info. `new Date(value)`
// parses them as local and `.toISOString()` converts correctly to UTC for the API —
// do NOT append 'Z' (that would mis-tag the value as already-UTC). Mirrors
// SoilGroupFieldPage.tsx's identical helper of the same name.
function localToIso(value: string): string {
  return new Date(value).toISOString();
}

interface CaptureState { startedAt: string; completedAt: string; ec: string; signature: string }
const EMPTY_CAPTURE: CaptureState = { startedAt: '', completedAt: '', ec: '', signature: '' };

// Covers both session statuses (draft/planned/in_progress/complete/signed_off/
// cancelled — backend/src/models/SoilApplicationSession.js:74) and group
// statuses (not_started/in_progress/complete/signed_off) — the two status
// vocabularies overlap and share a colour scheme on this screen. `cancelled`
// is reachable once Task 5 adds cancel-session to this page, so it must not
// fall through to the 'info' default (reads as a normal, still-active session).
const STATUS_VARIANT: Record<string, 'info' | 'ok' | 'warn' | 'danger'> = {
  draft: 'warn', not_started: 'info', planned: 'info',
  in_progress: 'warn', complete: 'ok', signed_off: 'ok', cancelled: 'danger',
};

const SoilSessionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // This route is gated on `fertilizing:sessions`, but the sign-off endpoint
  // requires `fertilizing:signoff` — a sessions-only user reaching this screen
  // gets a 403 from the button. Gate the affordance on the permission that
  // actually governs it (house convention: NutritionProductsPage.tsx:13).
  // `Record` stays ungated: completing a group is a `sessions` action.
  const canSignOff = useMenu('fertilizing:signoff');

  const [session, setSession] = useState<SoilSession | null>(null);
  const [pendingGroupIds, setPendingGroupIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Task 5 state — expand-to-capture, per-group sign-off, cancel-session.
  // All of it sits above the early returns below, same rule as the state above.
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [capture, setCapture] = useState<CaptureState>(EMPTY_CAPTURE);
  // Idempotency key for the in-flight (or about-to-be-retried) completeGroup
  // submit. Generated ONCE when a row is expanded for capture and reused on
  // every retry of that same submit so a lost response can never double-apply
  // — only cleared (and regenerated) when the row is freshly re-opened.
  const [captureKey, setCaptureKey] = useState('');
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureError, setCaptureError] = useState('');
  // Tenant-level 90-day override (#482). A read failure leaves it false, the
  // SAFE default — the user sees the warning and the server stays the authority.
  const [beyondOverrideOn, setBeyondOverrideOn] = useState(false);
  const [signoffError, setSignoffError] = useState('');
  // Guards against a double-click firing two signoff POSTs — harmless
  // server-side, but inconsistent with the busy-guards on capture and cancel.
  const [signoffBusyId, setSignoffBusyId] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // "Save this grouping as a set…" (U4 Task 7) — DETAIL page only, never the
  // builder: the endpoint reads the session's PERSISTED groups, and the
  // builder can be showing unsaved on-screen changes.
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsBusy, setSaveAsBusy] = useState(false);
  const [saveAsError, setSaveAsError] = useState('');
  const [saveAsSaved, setSaveAsSaved] = useState(false);

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  //
  // The Soil crumb MUST carry ?program=. This page is a sibling of the
  // workspace, not one of its tabs, so it does not share the layout's resolved
  // programme; the layout re-resolves it as "?program= if valid, else the
  // tenant's ACTIVE programme". A bare /nutrition/soil therefore drops the
  // programme and lands the user on a board they never came from. Falls back to
  // the bare path until the session (and its soilProgramId) has loaded —
  // useBreadcrumb keys on a projection that includes `to`, so it re-registers.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: t('nutrition.soil.workspace.title'),
      to: session ? `/nutrition/soil?program=${session.soilProgramId}` : '/nutrition/soil',
    },
    { label: session ? `${session.productName} · ${session.month}` : t('nutrition.soil.detail.crumb') },
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const { session: s } = await soilSessionService.get(id);
        if (cancelled) return;
        // A draft has nothing to show here — it belongs on the grouping
        // builder. Return BEFORE setSession so a draft never flashes this
        // screen even for a single frame.
        if (s.status === 'draft') { navigate(`/nutrition/soil/sessions/${id}/grouping`, { replace: true }); return; }
        // The offline-pending marker is a secondary signal (drives only the blue
        // "queued" row modifier) — a Dexie/IndexedDB failure here must cost only
        // the queued markers, never blank a session that loaded perfectly fine.
        const pending = await readPendingSoilGroupIds(id).catch(() => []);
        if (cancelled) return;
        setSession(s);
        setPendingGroupIds(pending);
        setError('');
      } catch (_e) {
        if (!cancelled) setError(t('nutrition.soil.detail.loadFailed'));
      }
    })();
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially
    // stable and would re-fire this effect every render, re-fetching forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let live = true;
    getTenantInfo()
      .then((tenant: any) => {
        if (!live) return;
        setBeyondOverrideOn(!!(tenant?.applicationSettings?.allowBackCaptureBeyond90Days
          ?? tenant?.spraySettings?.allowBackCaptureBeyond90Days));
      })
      .catch(() => { /* non-fatal: assume the override is off */ });
    return () => { live = false; };
  }, []);

  // Error branch returns BEFORE the loading guard, so a failed load renders a
  // notice rather than sitting on "loading" forever — but ONLY when there is
  // no session to show. `load()` below re-uses this same `error` after a
  // SUCCESSFUL capture or sign-off, so a full-page takeover here would blank
  // the table, the totals and the row the user just completed behind "we
  // couldn't load this session", implying the save failed when it did not.
  // With a session in hand the error renders inline above the table instead
  // — the same guard shape as SoilSignOffPage's `error && items === null`.
  if (error && !session) return <div className="pat-page" data-testid="det-error"><Notice tone="danger">{error}</Notice></div>;
  if (!session) return <div className="pat-page">{t('common.loading')}</div>;

  // Re-fetch entry point for every Task 5 mutation (capture / sign-off).
  // Re-fetch, never splice local state — the session snapshot returned by a
  // group mutation is not necessarily the same shape this screen renders, so
  // the single source of truth stays the GET. This is the mount effect's
  // fetch shape minus the unmount guard (the component is still mounted when
  // a handler calls this) and minus the draft redirect (a session that can
  // reach capture/sign-off/cancel from this screen is never a draft).
  const load = async () => {
    if (!id) return;
    try {
      const { session: s } = await soilSessionService.get(id);
      const pending = await readPendingSoilGroupIds(id).catch(() => []);
      setSession(s);
      setPendingGroupIds(pending);
      setError('');
    } catch (_e) {
      setError(t('nutrition.soil.detail.loadFailed'));
    }
  };

  const rows: DetailRow[] = buildDetailRows(session.groups || [], { pendingGroupIds, now: new Date() });
  const totals = detailTotals(rows);
  const ungrouped = ungroupedOrchards(session);
  const canCancel = session.status !== 'cancelled';

  const handleActionClick = (r: DetailRow) => {
    if (r.action === 'record') {
      if (expandedGroupId === r.groupId) {
        setExpandedGroupId(null);
        return;
      }
      setExpandedGroupId(r.groupId);
      // actionForStatus only offers 'record' for not_started/in_progress groups,
      // and ecReading/signatureName are written solely by completeGroup — so
      // both are always null on a group reachable here. Always start blank.
      setCapture(EMPTY_CAPTURE);
      // Fresh capture session — a fresh idempotency key. Reused as-is on
      // every retry until either a save succeeds or the row is re-opened.
      setCaptureKey(generateUUID());
      setCaptureError('');
    } else if (r.action === 'signoff') {
      handleSignOff(r.groupId);
    }
  };

  const handleSaveCapture = async (r: DetailRow) => {
    if (!id || !capture.completedAt) return;
    setCaptureBusy(true);
    setCaptureError('');
    try {
      // Payload construction (including two localToIso calls, which can throw
      // on an unparseable datetime) lives INSIDE the try/catch/finally. If it
      // sat before the try, a throw here would skip BOTH the catch and the
      // finally — captureBusy would stay true forever, permanently disabling
      // the form with no error shown.
      const ecTrimmed = capture.ec.trim();
      const ecValue = ecTrimmed === '' ? undefined : Number(ecTrimmed);
      const signatureTrimmed = capture.signature.trim();
      const payload: SoilGroupCompletePayload = {
        completedAt: localToIso(capture.completedAt),
        captureChannel: 'backcapture', // this screen is a manager transcribing, not the field
        completeKey: captureKey,
        // The backend holds the authoritative startedAt once a group was
        // started in the field (r.startedAt) — never send one in that case,
        // or a manager's back-capture would silently rewrite an audit value.
        ...(!r.startedAt && capture.startedAt ? { startedAt: localToIso(capture.startedAt) } : {}),
        ...(ecValue !== undefined && Number.isFinite(ecValue) ? { ecReading: ecValue } : {}),
        ...(signatureTrimmed ? { signatureName: signatureTrimmed } : {}),
      };
      await soilGroupService.completeGroup(id, r.groupId, payload);
      // Success: collapse and clear this group's capture state, then
      // re-fetch — never splice the row locally.
      setExpandedGroupId(null);
      setCapture(EMPTY_CAPTURE);
      setCaptureKey('');
      await load();
    } catch (_e) {
      // Failure: leave the row expanded with the user's values (and the same
      // completeKey) intact so a retry is a single click, not a re-type.
      setCaptureError(t('nutrition.soil.detail.saveFailed'));
    } finally {
      setCaptureBusy(false);
    }
  };

  const handleSignOff = async (groupId: string) => {
    if (!id || signoffBusyId) return;
    setSignoffBusyId(groupId);
    setSignoffError('');
    try {
      await soilGroupService.signOffGroup(id, groupId);
      await load();
    } catch (_e) {
      setSignoffError(t('nutrition.soil.detail.signOffFailed'));
    } finally {
      setSignoffBusyId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!id) return;
    setCancelBusy(true);
    setCancelError('');
    try {
      // The modal the user just clicked through IS the confirmation the
      // backend demands for a session already in_progress/complete/signed_off
      // — omitting `confirm: true` here 409s (CONFIRM_REQUIRED) every time,
      // unconditionally, for exactly the statuses this button is rendered for.
      await soilSessionService.cancel(id, { confirm: true });
      // Carry the programme back, for the same reason the breadcrumb does — a
      // bare /nutrition/soil returns the user to the tenant's ACTIVE board
      // rather than the one this session belonged to.
      navigate(session ? `/nutrition/soil?program=${session.soilProgramId}` : '/nutrition/soil');
    } catch (_e) {
      setCancelError(t('nutrition.soil.detail.cancelFailed'));
    } finally {
      setCancelBusy(false);
    }
  };

  const openSaveAs = () => {
    setSaveAsName('');
    setSaveAsError('');
    setSaveAsSaved(false);
    setSaveAsOpen(true);
  };

  const handleSaveAs = async () => {
    if (!id || !saveAsName.trim() || saveAsBusy) return;
    setSaveAsBusy(true);
    setSaveAsError('');
    try {
      await soilGroupSetService.saveAs(id, saveAsName.trim());
      setSaveAsSaved(true);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setSaveAsError(status === 409
        ? t('nutrition.soil.groupSets.dupName')
        : t('nutrition.soil.groupSets.saveFailed'));
    } finally {
      setSaveAsBusy(false);
    }
  };

  const renderCapture = (r: DetailRow) => (
    <div className="ntr-det-capture" data-testid={`det-capture-${r.groupId}`}>
      <FormSection id={`det-capture-form-${r.groupId}`} title={t('nutrition.soil.detail.capture')}>
        <Field
          label={t('nutrition.soil.detail.startedAt')}
          // controlId only when the editable input actually exists below —
          // in the read-only branch there is no matching element, and a
          // <label htmlFor> pointing at a non-existent id is broken markup.
          controlId={r.startedAt ? undefined : `det-startedAt-${r.groupId}`}
        >
          {r.startedAt ? (
            <span className="ntr-det-startedat-readonly" data-testid={`det-startedAt-readonly-${r.groupId}`}>
              {formatDateTime(r.startedAt)} · {t('nutrition.soil.detail.capturedInField')}
            </span>
          ) : (
            <input
              id={`det-startedAt-${r.groupId}`}
              type="datetime-local"
              className="pat-input"
              data-testid={`det-startedAt-${r.groupId}`}
              value={capture.startedAt}
              disabled={captureBusy}
              onChange={(e) => setCapture((c) => ({ ...c, startedAt: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('nutrition.soil.detail.completedAt')} required controlId={`det-completedAt-${r.groupId}`}>
          <input
            id={`det-completedAt-${r.groupId}`}
            type="datetime-local"
            required
            className="pat-input"
            data-testid={`det-completedAt-${r.groupId}`}
            value={capture.completedAt}
            disabled={captureBusy}
            onChange={(e) => setCapture((c) => ({ ...c, completedAt: e.target.value }))}
          />
        </Field>
        {/* This screen always posts captureChannel 'backcapture', so the bound
            always applies here. The FIELD channel is deliberately unbounded —
            see soilSignoffService.completeGroup. */}
        {isBackCaptureBlocked(capture.completedAt, beyondOverrideOn) && (
          <Notice tone="warn" testId="det-capture-too-old">
            {t('nutrition.soil.detail.captureTooOld')}
          </Notice>
        )}
        <Field label={t('nutrition.soil.detail.ec')} controlId={`det-ec-${r.groupId}`}>
          <input
            id={`det-ec-${r.groupId}`}
            type="number"
            min="0"
            step="0.1"
            className="pat-input"
            data-testid={`det-ec-${r.groupId}`}
            value={capture.ec}
            disabled={captureBusy}
            onChange={(e) => setCapture((c) => ({ ...c, ec: e.target.value }))}
          />
        </Field>
        <Field label={t('nutrition.soil.detail.signature')} controlId={`det-signature-${r.groupId}`}>
          <input
            id={`det-signature-${r.groupId}`}
            type="text"
            className="pat-input"
            data-testid={`det-signature-${r.groupId}`}
            value={capture.signature}
            disabled={captureBusy}
            onChange={(e) => setCapture((c) => ({ ...c, signature: e.target.value }))}
          />
        </Field>
        <div className="ntr-det-capture-actions">
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            data-testid={`det-capture-save-${r.groupId}`}
            disabled={
              !capture.completedAt
              || captureBusy
              || isBackCaptureBlocked(capture.completedAt, beyondOverrideOn)
            }
            onClick={() => handleSaveCapture(r)}
          >
            {t('nutrition.soil.detail.save')}
          </button>
          {captureError && (
            <Notice tone="danger" testId="det-capture-error">{captureError}</Notice>
          )}
        </div>
      </FormSection>
    </div>
  );

  return (
    <div className="pat-page" data-testid="soil-session-detail">
      <PageHeader
        title={`${session.productName} · ${session.month}`}
        meta={(
          <>
            <StatusPill variant={STATUS_VARIANT[session.status] ?? 'info'}>
              {t(`nutrition.soil.board.status.${session.status}`, session.status)}
            </StatusPill>{' '}
            {t('nutrition.soil.detail.meta', {
              groups: rows.length,
              orchards: (session.orchards || []).length,
              kg: formatMeasure(totals.plannedKg),
            })}
          </>
        )}
        actions={(
          <>
            {/* There is deliberately no "Edit grouping" action: SoilGroupingPage
                redirects every non-draft session straight back here, and this
                screen only ever renders non-draft sessions (a draft redirects
                away in the mount effect above). The button was a guaranteed
                no-op round trip. */}
            {/* This route is a stand-in: /nutrition/soil/sessions/:id/instruction does
                not exist yet — it belongs to a later slice. Navigating to the route
                that exists today (frontend/src/App.tsx:438) instead. Do not "fix"
                this to the tidier path until that route is actually built. */}
            <button
              type="button" className="pat-btn" data-testid="det-view-instruction"
              // Pass the programme through: the instruction page knows only the
              // session id (its payload carries no soilProgramId), so without
              // this its own Soil crumb would drop back to the ACTIVE board (#429).
              onClick={() => navigate(`/nutrition/soil-application-sessions/${id}/instruction?program=${session.soilProgramId}`)}
            >
              {t('nutrition.soil.detail.viewInstruction')}
            </button>
            {/* Hidden entirely when there is nothing to save — the endpoint
                reads the session's PERSISTED groups, and an empty session
                has none. */}
            {(session.groups || []).length > 0 && (
              <button type="button" className="pat-btn" data-testid="det-save-as-set" onClick={openSaveAs}>
                {t('nutrition.soil.groupSets.saveAs')}
              </button>
            )}
            {canCancel && (
              <button
                type="button" className="pat-btn pat-btn--danger" data-testid="det-cancel"
                onClick={() => setCancelOpen(true)}
              >
                {t('nutrition.soil.detail.cancelSession')}
              </button>
            )}
          </>
        )}
      />

      <div className="ntr-det-body">
        {/* Inline, not a full-page takeover: by here a session IS loaded, so
            this is a failed RELOAD after a save that may well have succeeded.
            Same testid as the full-page branch — the two are mutually
            exclusive, so only one is ever in the DOM. */}
        {error && <Notice tone="danger" testId="det-error">{error}</Notice>}
        {signoffError && <Notice tone="danger" testId="det-signoff-error">{signoffError}</Notice>}
        <DataTable<DetailRow>
          rows={rows}
          rowKey={(r) => r.groupId}
          rowClassName={rowModifierClass}
          expandedRowKey={expandedGroupId}
          expandedRowRender={renderCapture}
          emptyState={t('nutrition.soil.detail.noGroups')}
          columns={[
            { key: 'label', label: t('nutrition.soil.detail.col.group') },
            { key: 'orchardNames', label: t('nutrition.soil.detail.col.orchards'), render: (r) => r.orchardNames.join(', ') },
            { key: 'cultivars', label: t('nutrition.soil.detail.col.cultivars'), render: (r) => r.cultivars.join(', ') },
            { key: 'treeCount', label: t('nutrition.soil.detail.col.trees'), align: 'right', render: (r) => Math.round(r.treeCount) },
            { key: 'areaHa', label: t('nutrition.soil.detail.col.ha'), align: 'right', render: (r) => formatMeasure(r.areaHa) },
            { key: 'plannedKg', label: t('nutrition.soil.detail.col.kg'), align: 'right', render: (r) => formatMeasure(r.plannedKg) },
            { key: 'startedAt', label: t('nutrition.soil.detail.col.started'), render: (r) => (r.startedAt ? formatDateTime(r.startedAt) : '—') },
            { key: 'completedAt', label: t('nutrition.soil.detail.col.completed'), render: (r) => (r.completedAt ? formatDateTime(r.completedAt) : '—') },
            { key: 'ecReading', label: t('nutrition.soil.detail.col.ec'), align: 'right', render: (r) => (r.ecReading ?? '—') },
            { key: 'signatureName', label: t('nutrition.soil.detail.col.signature'), render: (r) => r.signatureName || '—' },
            { key: 'operators', label: t('nutrition.soil.detail.col.operator'), render: (r) => r.operators.join(', ') },
            {
              key: 'status',
              label: t('nutrition.soil.detail.col.status'),
              render: (r) => (
                <>
                  <StatusPill variant={STATUS_VARIANT[r.status] ?? 'info'}>{t(`nutrition.soil.groupStatus.${r.status}`)}</StatusPill>
                  {r.queued && <> <StatusPill variant="queued">{t('nutrition.soil.detail.queued')}</StatusPill></>}
                  {/* waitingDays === null means ageing does not apply (not complete,
                      or no completedAt) — it must NOT render as "0 days". 0 is a real
                      value: completed today, or clock skew.
                      The tone comes from soilSignOff.waitingTone, the same function
                      the sign-off queue uses, so info/warn/danger read identically on
                      both screens. The red row-stripe (rowModifierClass) is separate
                      and unchanged. */}
                  {r.waitingDays !== null && (
                    <>
                      {' '}
                      <span data-testid={`det-waiting-${r.groupId}`}>
                        <StatusPill variant={r.tone ?? 'info'}>
                          {t('nutrition.soil.detail.waiting', { count: r.waitingDays })}
                        </StatusPill>
                      </span>
                    </>
                  )}
                  {' '}
                  <span data-testid={`det-capture-provenance-${r.groupId}`}>
                    <NutritionBackCapturePill
                      captureMode={r.captureChannel === 'backcapture' ? 'back_capture' : 'live'}
                    />
                  </span>
                </>
              ),
            },
            {
              key: 'action',
              label: '',
              // A sign-off the caller cannot perform is not offered: the
              // endpoint 403s without fertilizing:signoff.
              render: (r) => (r.action === 'none' || (r.action === 'signoff' && !canSignOff) ? null : (
                <button
                  type="button" className="pat-btn pat-btn--subtle" data-testid={`det-action-${r.groupId}`}
                  disabled={r.action === 'signoff' && signoffBusyId === r.groupId}
                  onClick={() => handleActionClick(r)}
                >
                  {t(r.action === 'record' ? 'nutrition.soil.detail.record' : 'nutrition.soil.detail.signOff')}
                </button>
              )),
            },
          ]}
        />

        <div className="ntr-det-totals" data-testid="det-totals">
          {t('nutrition.soil.detail.totals', {
            kg: formatMeasure(totals.plannedKg), trees: Math.round(totals.treeCount),
            ha: formatMeasure(totals.areaHa), orchards: totals.orchardCount,
          })}
        </div>

        {/* Informational only. There is deliberately no "Fix grouping" link:
            the grouping builder redirects every non-draft session back here,
            and only non-draft sessions reach this screen, so the link was a
            guaranteed no-op. The disclosure itself stays — it is a
            data-integrity warning, and these orchards are silently excluded
            from the instruction otherwise. */}
        {ungrouped.length > 0 && (
          <div className="ntr-det-ungrouped" data-testid="det-ungrouped">
            {t('nutrition.soil.detail.ungrouped', { count: ungrouped.length, names: ungrouped.join(', ') })}
          </div>
        )}
      </div>

      {/*
        Modal does NOT accept a testid and does not spread props (its props
        are exactly show/onHide/title/size/dialogClassName/children) — a
        `data-testid` on <Modal> itself would silently vanish. Wrapping the
        body+footer content in this div carries the testid instead, and the
        confirm button test scopes `within(modal)` specifically because the
        header action button carries the same visible text ("Cancel session").
        Patterns.css has no `.pat-modal__dialog > .pat-modal__body`-style
        direct-child selector, so this extra wrapper div does not break the
        modal's layout.
      */}
      <Modal show={cancelOpen} onHide={() => setCancelOpen(false)} title={t('nutrition.soil.detail.cancelConfirmTitle')}>
        <div data-testid="det-cancel-modal">
          <Modal.Body>
            <p>{t('nutrition.soil.detail.cancelConfirmBody')}</p>
            {cancelError && <Notice tone="danger" testId="det-cancel-error">{cancelError}</Notice>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={() => setCancelOpen(false)}>
              {t('nutrition.soil.detail.cancelDismiss')}
            </button>
            <button
              type="button" className="pat-btn pat-btn--danger" disabled={cancelBusy}
              onClick={handleConfirmCancel}
            >
              {t('nutrition.soil.detail.cancelConfirm')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal show={saveAsOpen} onHide={() => setSaveAsOpen(false)} title={t('nutrition.soil.groupSets.saveAs')}>
        <div data-testid="det-save-as-modal">
          <Modal.Body>
            <Field label={t('nutrition.soil.groupSets.nameLabel')} required controlId="det-set-name">
              <input
                id="det-set-name"
                type="text"
                className="pat-input"
                data-testid="det-set-name"
                value={saveAsName}
                disabled={saveAsBusy}
                onChange={(e) => setSaveAsName(e.target.value)}
              />
            </Field>
            {saveAsError && <Notice tone="danger" testId="det-save-as-error">{saveAsError}</Notice>}
            {saveAsSaved && <Notice tone="success" testId="det-set-saved">{t('nutrition.soil.groupSets.saved')}</Notice>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={() => setSaveAsOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button" className="pat-btn pat-btn--primary"
              disabled={!saveAsName.trim() || saveAsBusy}
              onClick={handleSaveAs}
            >
              {t('nutrition.soil.groupSets.saveAsConfirm')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default SoilSessionDetailPage;
